import React from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  Container,
  Alert,
  AlertTitle,
  Divider,
  Stack,
} from "@mui/material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import RefreshIcon from "@mui/icons-material/Refresh";
import errorLogger from "../../utils/errorLogger";

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null,
      errorCount: 0,
    };
    this.isHandlingError = false; // Prevent infinite loops
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      error: error,
      errorId: Date.now().toString(36) + Math.random().toString(36).substr(2),
    };
  }

  componentDidMount() {
    // Set up global error handlers to catch async errors and event handler errors
    this.setupGlobalErrorHandlers();

    // Override React's error overlay in development
    if (process.env.NODE_ENV === "development") {
      this.overrideReactErrorOverlay();
    }
  }

  componentWillUnmount() {
    // Clean up global error handlers
    this.cleanupGlobalErrorHandlers();
  }

  overrideReactErrorOverlay = () => {
    // Try to override React's error overlay
    try {
      // Override console.error to prevent React's error overlay from showing
      const originalConsoleError = console.error;
      console.error = (...args) => {
        // Check if this is a React error that should be handled by our boundary
        const errorMessage = args.join(" ");
        if (
          errorMessage.includes("Error:") ||
          errorMessage.includes("TypeError:") ||
          errorMessage.includes("RangeError:")
        ) {
          // Don't call original console.error to prevent React overlay
          return;
        }
        // Call original for other console errors
        originalConsoleError.apply(console, args);
      };
    } catch (e) {
      console.warn("Could not override React error overlay:", e);
    }
  };

  setupGlobalErrorHandlers = () => {
    // Handle unhandled promise rejections
    this.unhandledRejectionHandler = (event) => {
      if (!this.isHandlingError && !this.state.hasError) {
        console.error("Unhandled promise rejection:", event.reason);
        this.handleGlobalError(event.reason, "Unhandled Promise Rejection");
      }
    };

    // Handle global errors (including async errors)
    this.globalErrorHandler = (event) => {
      if (!this.isHandlingError && !this.state.hasError) {
        console.error("Global error:", event.error);
        this.handleGlobalError(event.error, "Global Error");
      }
    };

    // Add event listeners
    window.addEventListener(
      "unhandledrejection",
      this.unhandledRejectionHandler
    );
    window.addEventListener("error", this.globalErrorHandler);
  };

  cleanupGlobalErrorHandlers = () => {
    // Remove event listeners
    if (this.unhandledRejectionHandler) {
      window.removeEventListener(
        "unhandledrejection",
        this.unhandledRejectionHandler
      );
    }
    if (this.globalErrorHandler) {
      window.removeEventListener("error", this.globalErrorHandler);
    }
  };

  handleGlobalError = (error, errorType) => {
    // Prevent infinite loops
    if (this.isHandlingError || this.state.hasError) {
      return;
    }

    this.isHandlingError = true;

    try {
      this.setState((prevState) => ({
        hasError: true,
        error: error,
        errorInfo: {
          componentStack: error.stack || "No stack trace available",
        },
        errorId: Date.now().toString(36) + Math.random().toString(36).substr(2),
        errorCount: prevState.errorCount + 1,
      }));

      // Log error using centralized error logger (but don't let it cause more errors)
      try {
        errorLogger.logError(
          error,
          { componentStack: error.stack },
          "GlobalErrorBoundary",
          {
            boundaryType: "global",
            errorType: errorType,
            componentStack: error.stack,
            errorCount: this.state.errorCount + 1,
          }
        );
      } catch (logError) {
        console.warn("Error logging failed:", logError);
      }
    } catch (setStateError) {
      console.error("Error setting state in error boundary:", setStateError);
    } finally {
      this.isHandlingError = false;
    }
  };

  componentDidCatch(error, errorInfo) {
    // Prevent infinite loops
    if (this.isHandlingError) {
      return;
    }

    this.isHandlingError = true;

    try {
      // Log error to console for debugging (but don't let React overlay show)
      if (process.env.NODE_ENV === "development") {
        // Use console.warn instead of console.error to avoid React overlay
        console.warn(
          "Global Error Boundary caught an error:",
          error,
          errorInfo
        );
      } else {
        console.error(
          "Global Error Boundary caught an error:",
          error,
          errorInfo
        );
      }

      // Update state with error info
      this.setState((prevState) => ({
        errorInfo: errorInfo,
        errorCount: prevState.errorCount + 1,
      }));

      // Log error using centralized error logger (but don't let it cause more errors)
      try {
        errorLogger.logError(error, errorInfo, "GlobalErrorBoundary", {
          boundaryType: "global",
          componentStack: errorInfo?.componentStack,
          errorCount: this.state.errorCount + 1,
        });
      } catch (logError) {
        console.warn("Error logging failed:", logError);
      }
    } catch (setStateError) {
      console.error("Error in componentDidCatch:", setStateError);
    } finally {
      this.isHandlingError = false;
    }
  }

  handleRetry = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null,
      errorCount: 0,
    });
    this.isHandlingError = false;
  };

  handleGoHome = () => {
    window.location.href = "/";
  };

  handleRefresh = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <Container maxWidth="md" sx={{ py: 4 }}>
          <Paper
            elevation={3}
            sx={{
              p: 4,
              textAlign: "center",
              borderRadius: 2,
              border: "1px solid #e0e0e0",
              backgroundColor: "#fff",
            }}
          >
            <Stack spacing={3} alignItems="center">
              {/* Error Icon */}
              <ErrorOutlineIcon
                sx={{
                  fontSize: 80,
                  color: "#f44336",
                  mb: 2,
                }}
              />

              {/* Main Error Message */}
              <Typography variant="h4" component="h1" gutterBottom>
                Oops! Something went wrong
              </Typography>

              {/* Error Details (for development) */}
              {this.state.error && (
                <Alert
                  severity="error"
                  sx={{ width: "100%", textAlign: "left" }}
                >
                  <AlertTitle>Error Details</AlertTitle>
                  <Typography
                    variant="body2"
                    component="pre"
                    sx={{
                      fontSize: "0.75rem",
                      overflow: "auto",
                      maxHeight: "200px",
                      backgroundColor: "#f5f5f5",
                      p: 1,
                      borderRadius: 1,
                    }}
                  >
                    {this.state.error.toString()}
                  </Typography>
                  {this.state.errorInfo && (
                    <Typography
                      variant="body2"
                      component="pre"
                      sx={{
                        fontSize: "0.75rem",
                        overflow: "auto",
                        maxHeight: "200px",
                        backgroundColor: "#f5f5f5",
                        p: 1,
                        borderRadius: 1,
                        mt: 1,
                      }}
                    >
                      {this.state.errorInfo.componentStack}
                    </Typography>
                  )}
                </Alert>
              )}

              <Divider sx={{ width: "100%", my: 2 }} />

              {/* Action Buttons */}
              <Stack
                direction="row"
                spacing={2}
                sx={{ mt: 2, flexWrap: "wrap", justifyContent: "center" }}
              >
                <Button
                  variant="contained"
                  startIcon={<RefreshIcon />}
                  onClick={this.handleRetry}
                  sx={{
                    backgroundColor: "#1976d2",
                    "&:hover": {
                      backgroundColor: "#1565c0",
                    },
                  }}
                >
                  Try Again
                </Button>
              </Stack>

              {/* Additional Help */}
              <Box
                sx={{
                  mt: 3,
                  p: 2,
                  backgroundColor: "#f8f9fa",
                  borderRadius: 1,
                  width: "100%",
                }}
              >
                {/* <Typography variant="body2" color="text.secondary">
                  If the problem persists, please contact our support team with
                  the Error ID above.
                </Typography> */}
              </Box>
            </Stack>
          </Paper>
        </Container>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
